const getFaviconLinks = () =>
  Array.from(document.querySelectorAll('link[data-managed-favicon="true"]'));

const getSizeToken = favicon => {
  return favicon.getAttribute('sizes') || '32x32';
};

const getCanvasSize = favicon => {
  const sizeToken = getSizeToken(favicon);
  const parsed = Number.parseInt(String(sizeToken).split('x')[0], 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 32;
};

const shouldUseCrossOrigin = sourceHref => {
  return /^https?:\/\//i.test(sourceHref) || sourceHref.startsWith('//');
};

const drawImageContain = (context, image, size) => {
  const width = image.naturalWidth || image.width || size;
  const height = image.naturalHeight || image.height || size;
  if (!width || !height) return;

  const scale = Math.min(size / width, size / height);
  const drawWidth = width * scale;
  const drawHeight = height * scale;
  const offsetX = (size - drawWidth) / 2;
  const offsetY = (size - drawHeight) / 2;

  context.clearRect(0, 0, size, size);
  context.drawImage(image, offsetX, offsetY, drawWidth, drawHeight);
};

const badgeCache = new Map();
let badgeMutationToken = 0;

const createBadgedFavicon = (sourceHref, size) => {
  const cacheKey = `${sourceHref}|${size}`;
  if (badgeCache.has(cacheKey)) {
    return badgeCache.get(cacheKey);
  }

  const renderPromise = new Promise(resolve => {
    const image = new Image();
    if (shouldUseCrossOrigin(sourceHref)) {
      image.crossOrigin = 'anonymous';
    }

    image.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const context = canvas.getContext('2d');
        if (!context) {
          resolve(null);
          return;
        }

        drawImageContain(context, image, size);

        const outerRadius = Math.max(3, Math.round(size * 0.24));
        const innerRadius = Math.max(
          2,
          outerRadius - Math.max(1, Math.round(size * 0.05))
        );
        const centerX =
          size - outerRadius - Math.max(1, Math.round(size * 0.06));
        const centerY = outerRadius + Math.max(1, Math.round(size * 0.06));

        context.beginPath();
        context.fillStyle = '#ffffff';
        context.arc(centerX, centerY, outerRadius, 0, Math.PI * 2);
        context.fill();

        context.beginPath();
        context.fillStyle = '#ef4444';
        context.arc(centerX, centerY, innerRadius, 0, Math.PI * 2);
        context.fill();

        resolve(canvas.toDataURL('image/png'));
      } catch {
        resolve(null);
      }
    };

    image.onerror = () => resolve(null);
    image.src = sourceHref;
  });

  badgeCache.set(cacheKey, renderPromise);
  return renderPromise;
};

const restoreBaseFavicon = () => {
  badgeMutationToken += 1;
  getFaviconLinks().forEach(favicon => {
    const fallbackHref =
      favicon.dataset.baseHref || favicon.dataset.defaultHref || favicon.href;

    if (fallbackHref) {
      favicon.href = fallbackHref;
    }
  });
};

export const showBadgeOnFavicon = () => {
  badgeMutationToken += 1;
  const currentToken = badgeMutationToken;

  getFaviconLinks().forEach(async favicon => {
    const sourceHref =
      favicon.dataset.baseHref || favicon.dataset.defaultHref || favicon.href;
    if (!sourceHref) return;

    const badgedHref = await createBadgedFavicon(
      sourceHref,
      getCanvasSize(favicon)
    );

    if (!badgedHref || currentToken !== badgeMutationToken) {
      return;
    }

    favicon.href = badgedHref;
  });
};

let hasBoundVisibilityListener = false;

export const initFaviconSwitcher = () => {
  if (hasBoundVisibilityListener) return;

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      restoreBaseFavicon();
    }
  });

  hasBoundVisibilityListener = true;
};
